// Web Audio API synthesized sound effects
// Beam: gentle, soft sci-fi hum (low sine pad + slow tremolo + lowpass filter).
// All sounds are gated by a global mute flag persisted to localStorage.

const MUTE_KEY = "zombieCollector3D_muted_v1";

let ctx = null;
let masterGain = null; // ducked when muted

function getCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      masterGain = ctx.createGain();
      masterGain.gain.value = isMuted() ? 0 : 1;
      masterGain.connect(ctx.destination);
    } catch (e) {
      return null;
    }
  }
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

export function isMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function setMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch (e) {}
  if (ctx && masterGain) {
    const t = ctx.currentTime;
    masterGain.gain.cancelScheduledValues(t);
    masterGain.gain.linearRampToValueAtTime(muted ? 0 : 1, t + 0.08);
  }
  if (muted) stopBeam();
}

export function toggleMuted() {
  const next = !isMuted();
  setMuted(next);
  return next;
}

function out() {
  return masterGain || getCtx().destination;
}

function tone({ freq = 440, dur = 0.15, type = "sine", gain = 0.15, sweep = 0, delay = 0 }) {
  const c = getCtx();
  if (!c) return;
  const t = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (sweep !== 0) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(freq + sweep, 30), t + dur);
  }
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g);
  g.connect(out());
  osc.start(t);
  osc.stop(t + dur + 0.05);
}

// --------------------- BEAM (gentle, soft sci-fi hum) ---------------------
// Architecture:
//   • two detuned low sine oscillators (110 + 165Hz, a soft perfect fifth)
//   • routed through a lowpass filter (~900Hz) for an "underwater" softness
//   • a slow 3.5Hz tremolo via LFO -> tremGain (very subtle)
//   • starts at gain 0 and fades to 0.045 over 0.4s (no pop)
//   • a thin, low-volume filtered noise layer gives a gentle "whoosh"
let beamNodes = null;

export function playBeamStart() {
  const c = getCtx();
  if (!c) return;
  stopBeam(true);

  const now = c.currentTime;

  // Carrier gain (very quiet)
  const carrier = c.createGain();
  carrier.gain.setValueAtTime(0, now);
  carrier.gain.linearRampToValueAtTime(0.045, now + 0.4);

  // Lowpass filter for softness
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(900, now);
  filter.Q.setValueAtTime(0.7, now);

  // Two detuned sines — gentle and harmonic
  const osc1 = c.createOscillator();
  osc1.type = "sine";
  osc1.frequency.setValueAtTime(110, now);
  const osc2 = c.createOscillator();
  osc2.type = "sine";
  osc2.frequency.setValueAtTime(165, now); // perfect 5th
  const osc3 = c.createOscillator();
  osc3.type = "triangle";
  osc3.frequency.setValueAtTime(220, now);

  // Mix oscillators into the carrier through the lowpass
  const oscMix = c.createGain();
  oscMix.gain.value = 0.6;
  osc1.connect(oscMix);
  osc2.connect(oscMix);
  const oscMixTri = c.createGain();
  oscMixTri.gain.value = 0.18; // triangle is louder perceptually
  osc3.connect(oscMixTri);
  oscMix.connect(filter);
  oscMixTri.connect(filter);
  filter.connect(carrier);

  // Slow tremolo (3.5Hz, ±15% amplitude) for a "spacey" wobble
  const lfo = c.createOscillator();
  lfo.frequency.setValueAtTime(3.5, now);
  const lfoGain = c.createGain();
  lfoGain.gain.value = 0.012;
  lfo.connect(lfoGain).connect(carrier.gain);

  // Very thin noise layer for a soft whoosh
  // Use a short pre-generated buffer of pink-ish noise
  const noiseBuf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
  const data = noiseBuf.getChannelData(0);
  let lastOut = 0;
  for (let i = 0; i < data.length; i++) {
    const white = Math.random() * 2 - 1;
    // simple low-pass to make it pink-ish
    lastOut = (lastOut + 0.02 * white) / 1.02;
    data[i] = lastOut * 6;
  }
  const noise = c.createBufferSource();
  noise.buffer = noiseBuf;
  noise.loop = true;
  const noiseFilter = c.createBiquadFilter();
  noiseFilter.type = "bandpass";
  // Slightly wider band centered a touch lower for an airier "suction" tone
  noiseFilter.frequency.setValueAtTime(340, now);
  noiseFilter.Q.setValueAtTime(0.55, now);
  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0, now);
  // Slightly more whoosh — still soft but more "suction beam" feel
  noiseGain.gain.linearRampToValueAtTime(0.034, now + 0.55);
  noise.connect(noiseFilter).connect(noiseGain).connect(out());

  carrier.connect(out());

  osc1.start(now);
  osc2.start(now);
  osc3.start(now);
  lfo.start(now);
  noise.start(now);

  beamNodes = { osc1, osc2, osc3, lfo, noise, carrier, noiseGain };
}

export function stopBeam(instant = false) {
  const c = getCtx();
  if (!c || !beamNodes) return;
  const { osc1, osc2, osc3, lfo, noise, carrier, noiseGain } = beamNodes;
  const now = c.currentTime;
  const fade = instant ? 0.02 : 0.18;
  try {
    carrier.gain.cancelScheduledValues(now);
    carrier.gain.setValueAtTime(carrier.gain.value, now);
    carrier.gain.linearRampToValueAtTime(0, now + fade);
    noiseGain.gain.cancelScheduledValues(now);
    noiseGain.gain.setValueAtTime(noiseGain.gain.value, now);
    noiseGain.gain.linearRampToValueAtTime(0, now + fade);
    osc1.stop(now + fade + 0.05);
    osc2.stop(now + fade + 0.05);
    osc3.stop(now + fade + 0.05);
    lfo.stop(now + fade + 0.05);
    noise.stop(now + fade + 0.05);
  } catch (e) {}
  beamNodes = null;
}

// --------------------- ONE-SHOT SFX ---------------------

export function playCollect() {
  // Soft "scoop up" — short, light triangle blip
  tone({ freq: 520, dur: 0.09, type: "triangle", gain: 0.06, sweep: 320 });
  tone({ freq: 720, dur: 0.11, type: "sine", gain: 0.05, sweep: 240, delay: 0.06 });
}

export function playCoin() {
  tone({ freq: 880, dur: 0.07, type: "sine", gain: 0.05 });
  tone({ freq: 1320, dur: 0.1, type: "sine", gain: 0.05, delay: 0.06 });
}

export function playWin() {
  tone({ freq: 523, dur: 0.18, type: "triangle", gain: 0.09 });
  tone({ freq: 659, dur: 0.18, type: "triangle", gain: 0.09, delay: 0.18 });
  tone({ freq: 784, dur: 0.18, type: "triangle", gain: 0.09, delay: 0.36 });
  tone({ freq: 1047, dur: 0.32, type: "triangle", gain: 0.09, delay: 0.54 });
}

export function playLose() {
  tone({ freq: 392, dur: 0.22, type: "triangle", gain: 0.08, sweep: -180 });
  tone({ freq: 262, dur: 0.42, type: "triangle", gain: 0.08, sweep: -120, delay: 0.18 });
}

export function playLaser() {
  const c = getCtx();
  if (!c) return;
  const t = c.currentTime;
  // Short downward chirp + soft sine click — feels like a beam zap
  tone({ freq: 1600, dur: 0.05, type: "sine", gain: 0.04, sweep: -1200 });
  tone({ freq: 600, dur: 0.04, type: "triangle", gain: 0.03, delay: 0.01 });
}

export function playLightning() {
  const c = getCtx();
  if (!c) return;
  // Soft purple-white "crackle": short noise burst + brief downward sine
  tone({ freq: 880, dur: 0.06, type: "sine", gain: 0.04, sweep: -560 });
  tone({ freq: 320, dur: 0.05, type: "triangle", gain: 0.03, delay: 0.02 });
}

export function playClick() {
  tone({ freq: 660, dur: 0.04, type: "sine", gain: 0.04 });
}

export function playPurchase() {
  tone({ freq: 600, dur: 0.08, type: "sine", gain: 0.06 });
  tone({ freq: 900, dur: 0.1, type: "sine", gain: 0.06, delay: 0.07 });
  tone({ freq: 1200, dur: 0.14, type: "sine", gain: 0.06, delay: 0.14 });
}

// ----- Reward-power impact SFX (throttled by caller) -----
export function playWheelHit() {
  // Quick metallic clink
  tone({ freq: 720, dur: 0.05, type: "square", gain: 0.025, sweep: -240 });
  tone({ freq: 1100, dur: 0.04, type: "triangle", gain: 0.02, delay: 0.01 });
}

export function playBallHit() {
  // Rubbery bounce
  tone({ freq: 380, dur: 0.07, type: "sine", gain: 0.035, sweep: 180 });
  tone({ freq: 520, dur: 0.05, type: "triangle", gain: 0.025, delay: 0.04 });
}

export function playDogBark() {
  // Short happy yip
  tone({ freq: 740, dur: 0.06, type: "triangle", gain: 0.04, sweep: -260 });
  tone({ freq: 540, dur: 0.05, type: "triangle", gain: 0.03, delay: 0.05 });
}

export function playEpicReveal() {
  // A little chime when an epic-rarity card is revealed
  tone({ freq: 880, dur: 0.18, type: "triangle", gain: 0.07 });
  tone({ freq: 1320, dur: 0.22, type: "triangle", gain: 0.06, delay: 0.08 });
  tone({ freq: 1760, dur: 0.28, type: "sine", gain: 0.05, delay: 0.18 });
}
